import React from 'react';
import { arrowChoice, useIndicator } from './useIndicator';

/* ============================================================================
   The primitives every screen is built from. The values behind them are the
   tokens in theme.css; the rules for using them are docs/product/DESIGN-SYSTEM.md.

   · The page is a cool slate ground; content sits on surfaces with a hairline
     and a whisper of lift
   · A control is a crisp rounded rectangle — never a pill
   · Action navy means "act here" or "selected"; jade means money that is
     settled, collected or current; nothing is coloured for decoration
   ============================================================================ */

export const cx = (...parts: (string | false | null | undefined)[]) =>
    parts.filter(Boolean).join(' ');

/* --------------------------------- Button -------------------------------- */

/**
 * - `primary`: the one thing this screen or form is for. One per region.
 * - `secondary`: every other action — a surface, edged, ink text.
 * - `quiet` (and its older name `ghost`): an action that should not compete —
 *   Clear, Cancel beside a form, a toolbar toggle.
 * - `link`: an action inside text, or one that reads as navigation.
 * - `danger`: a page-level control that leads to something destructive.
 * - `destructive`: the final answer in a question dialog, the one that does it.
 * - `inverse`: the primary action on the navy chrome or a dark section of the
 *   site — white, navy text.
 * - `row`: an action on a row of a list or table — words in ink, no edge, the
 *   product's hover (2026-10 global sweep). A row of outlined buttons on every
 *   line of a list is the heaviest thing on the page; the row's actions are not.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'ghost' | 'link' | 'danger' | 'destructive' | 'inverse' | 'row';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
    primary: 'bg-accent text-on-accent shadow-e1 hover:bg-accent-press active:bg-accent-press',
    secondary: 'bg-card text-label border border-separator-strong shadow-e0 hover:bg-hover hover:border-label-4 active:bg-press',
    quiet: 'bg-transparent text-label-2 hover:bg-hover hover:text-label active:bg-press',
    ghost: 'bg-transparent text-label-2 hover:bg-hover hover:text-label active:bg-press',
    link: 'bg-transparent text-accent-text underline-offset-4 hover:underline !h-auto !px-0',
    danger: 'bg-card text-dang border border-separator-strong shadow-e0 hover:bg-dang-bg active:bg-dang-bg',
    destructive: 'bg-dang-solid text-on-accent shadow-e1 hover:bg-dang-solid-press active:bg-dang-solid-press',
    inverse: 'bg-inverse text-on-inverse shadow-e1 hover:bg-inverse-hover active:bg-inverse-hover',
    row: 'bg-transparent text-label hover:bg-hover hover:text-accent-text active:bg-press',
};

/* 32 / 36 / 44 / 48. `md` sits in toolbars, forms and tables; `lg` is for the
   sign-in and set-up forms, where the page is the form; `xl` is the site's. */
const BUTTON_SIZE: Record<ButtonSize, string> = {
    sm: 'h-8 px-3 text-secondary gap-1.5',
    md: 'h-9 px-3.5 text-body gap-2',
    lg: 'h-11 px-5 text-subhead gap-2',
    xl: 'h-12 px-6 text-subhead gap-2.5',
};

/** The classes of a button, for anything that must look like one — a real link included. */
export function buttonClass({ variant = 'secondary', size = 'md', className }: {
    variant?: ButtonVariant; size?: ButtonSize; className?: string | undefined;
} = {}) {
    return cx(
        'inline-flex items-center justify-center font-semibold whitespace-nowrap rounded-control select-none transition-[background-color,border-color,color,box-shadow] duration-150',
        'disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50',
        BUTTON_SIZE[size],
        BUTTON_VARIANT[variant],
        className,
    );
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    icon?: React.ReactNode;
    /** Working on it: disabled, announced as busy, and a spinner in place of the icon. */
    busy?: boolean;
}

export const Button = ({
    variant = 'secondary',
    size = 'md',
    icon,
    busy = false,
    disabled,
    className,
    children,
    ...rest
}: ButtonProps) => (
    <button
        {...rest}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
        className={buttonClass({ variant, size, className })}
    >
        {busy ? <Spinner /> : icon}
        {children}
    </button>
);

/** A button that is only an icon. It must say what it does, for the ear and the tooltip. */
export const IconButton = ({
    label, size = 'md', variant = 'quiet', className, children, ...rest
}: Omit<ButtonProps, 'icon' | 'busy' | 'aria-label'> & { label: string }) => (
    <button
        {...rest}
        type={rest.type ?? 'button'}
        aria-label={label}
        title={label}
        className={cx(buttonClass({ variant, size }), size === 'sm' ? '!w-8 !px-0' : size === 'lg' ? '!w-11 !px-0' : size === 'xl' ? '!w-12 !px-0' : '!w-9 !px-0', className)}
    >
        {children}
    </button>
);

/* --------------------------------- Fields -------------------------------- */

/**
 * A text field, select or text area: one height, one edge, one focus.
 * `md` (36px) sits in toolbars and working forms; `lg` (44px) is the sign-in
 * and set-up forms'. Focus is the accent edge and a soft halo; the global
 * outline is turned off here so the two never stack.
 */
export function fieldClass({ size = 'md', invalid = false, multiline = false, className }: {
    size?: 'md' | 'lg'; invalid?: boolean; multiline?: boolean; className?: string | undefined;
} = {}) {
    return cx(
        'rounded-control bg-card border text-label shadow-e0 transition-[border-color,box-shadow] duration-150',
        'hover:border-label-4 focus:outline-none focus-visible:outline-none focus:ring-3',
        'disabled:bg-card-2 disabled:text-label-3 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:border-separator-strong',
        multiline ? 'px-3 py-2 leading-snug' : size === 'lg' ? 'h-11 px-3.5' : 'h-9 px-3',
        size === 'lg' ? 'text-subhead' : 'text-body',
        invalid
            ? 'border-dang focus:border-dang focus:ring-dang-bg'
            : 'border-separator-strong focus:border-accent focus:ring-accent-tint-2',
        className,
    );
}

/** A field's label: above it, in words, never a placeholder standing in. */
export const labelClass = 'block text-secondary font-medium text-label mb-1.5';

/** A filter's label in a toolbar: small, above the control, so a row of filters reads as one bar. */
export const filterLabelClass = 'block text-caption font-medium text-label-3 mb-1';

/** A line under a field that removes doubt: what to type, or what happens next. */
export const helpClass = 'text-caption text-label-3 mt-1.5';

/** A field's error, under it, in words that say how to fix it. */
export const errorClass = 'text-secondary text-dang mt-1.5';

/* -------------------------------- Overlays ------------------------------- */

/**
 * Everything that floats over the page is one material (DESIGN-SYSTEM §11): a menu, a list of choices, a
 * calendar, a customer search's suggestions — and, drawn by the browser from theme.css, a select's open list. The
 * card surface, its hairline, the card radius, the floating shadow, rising into place.
 */
export const popoverClass = 'bg-card text-label border border-separator rounded-card shadow-e2 animate-rise';

/** One row in a floating list: inset from the popover's edge, its own small radius, the product's hover. */
export const popoverItemClass = cx(
    'flex items-center gap-2 w-full min-h-9 px-2.5 rounded-sm text-left text-body',
    'hover:bg-hover focus:bg-hover focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed',
);

/** A small capitalised label over a group or a figure ("OUTSTANDING"). */
export const eyebrowClass = 'text-caption font-semibold uppercase tracking-caps text-label-3';

/* --------------------------------- Badge --------------------------------- */

/**
 * A short status beside a name: "Urgent", "In credit". Text first — the words
 * carry the meaning and the tone only supports it — and only where a status
 * would otherwise be missed. Most values are plain text. `dot` adds the settle
 * square before the words, for a status that should read at a glance.
 */
export type Tone = 'neutral' | 'brand' | 'pos' | 'warn' | 'dang' | 'info' | 'mark' | 'age1' | 'age2' | 'age3' | 'age4';

const TONE: Record<Tone, string> = {
    neutral: 'bg-card-3 text-label-2',
    brand: 'bg-accent-tint text-accent-text',
    pos: 'bg-pos-bg text-pos',
    warn: 'bg-warn-bg text-warn',
    dang: 'bg-dang-bg text-dang',
    info: 'bg-info-bg text-info',
    mark: 'bg-mark-tint text-pos',
    age1: 'bg-age-1-bg text-age-1-ink',
    age2: 'bg-age-2-bg text-age-2-ink',
    age3: 'bg-age-3-bg text-age-3-ink',
    age4: 'bg-age-4-bg text-age-4-ink',
};

/** The same tones as words alone: no fill, for a status that is a column's value or a name's quiet aside. */
const TEXT_TONE: Record<Tone, string> = {
    neutral: 'text-label-3',
    brand: 'text-accent-text',
    pos: 'text-pos',
    warn: 'text-warn',
    dang: 'text-dang',
    info: 'text-info',
    mark: 'text-pos',
    age1: 'text-age-1-ink',
    age2: 'text-age-2-ink',
    age3: 'text-age-3-ink',
    age4: 'text-age-4-ink',
};

/**
 * `variant="soft"` (the default) is a filled tag — for a flag that must be noticed in a row of other text
 * ("Urgent", "Promise not kept"). `variant="text"` is the same words without a box (2026-10 global sweep): a
 * status that is the value of a column (a cheque's state, a setup step's), a category, a channel, "Built in".
 * A page where every line carries a pill reads as a component demo; most statuses are words.
 */
export const Badge = ({
    tone = 'neutral',
    children,
    className,
    title,
    dot = false,
    variant = 'soft',
}: {
    tone?: Tone;
    children: React.ReactNode;
    className?: string;
    title?: string;
    dot?: boolean;
    variant?: 'soft' | 'text';
}) => (
    <span
        title={title}
        className={cx(
            'inline-flex items-center gap-1.5 text-caption font-semibold leading-none whitespace-nowrap',
            variant === 'soft' ? cx('rounded-sm px-2 h-5', TONE[tone]) : cx('h-5', TEXT_TONE[tone]),
            className,
        )}
    >
        {dot && <span aria-hidden="true" className="size-1.5 flex-none bg-current" />}
        {children}
    </span>
);

/* ---------------------------------- Card --------------------------------- */

/**
 * A surface: a hairline edge, the card radius and a whisper of lift. Use one
 * only for something that is a thing in itself — a list, a form, a report —
 * never around each line of text, and never a card inside a card.
 */
export const Card = ({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) => (
    <div {...rest} className={cx('bg-card border border-separator rounded-card shadow-e0', className)}>
        {children}
    </div>
);

/**
 * A surface with a header: a title, a line saying what it holds, and the
 * controls that change it on the same row — never a control floating above a
 * section it may or may not govern.
 */
export const Panel = ({
    title, description, actions, headingLevel = 2, className, bodyClassName, children, id,
}: {
    title: React.ReactNode;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    headingLevel?: 2 | 3;
    className?: string;
    bodyClassName?: string;
    children: React.ReactNode;
    id?: string;
}) => {
    const H = headingLevel === 3 ? 'h3' : 'h2';
    return (
        <section aria-labelledby={id} className={cx('bg-card border border-separator rounded-card shadow-e0 min-w-0', className)}>
            {/* No rule under the heading: the heading and the space after it already say where the body starts. A
                table's own header band, or a list's first row, follows directly. */}
            <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-x-6 gap-y-2 flex-wrap min-h-14">
                <div className="min-w-0">
                    <H id={id} className="font-display stretch-title text-heading font-semibold text-label tracking-heading">{title}</H>
                    {description && <p className="text-secondary text-label-3 mt-0.5">{description}</p>}
                </div>
                {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
            </div>
            <div className={bodyClassName}>{children}</div>
        </section>
    );
};

/**
 * One group of settings on a page of settings (2026-10 refinement): what it is and why on the left, its fields
 * on the right, from 1280px; stacked below. No card around it — groups are divided by one hairline and the space
 * around it, so a page of ten settings reads as one form rather than ten boxes. The fields keep a readable width.
 */
export const FormSection = ({
    title, description, children, className, wide = false,
}: {
    title: React.ReactNode;
    description?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    /** Content that needs the whole column (a list, a table) rather than a form's measure. */
    wide?: boolean;
}) => {
    const id = React.useId();
    return (
        <section aria-labelledby={id} className={cx(
            'grid gap-x-10 gap-y-4 py-7 border-t border-separator first:border-t-0 first:pt-1 xl:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]',
            className,
        )}>
            <div className="min-w-0">
                <h3 id={id} className="font-display stretch-title text-heading font-semibold text-label tracking-heading">{title}</h3>
                {description && <p className="mt-1 text-secondary text-label-3 leading-relaxed max-w-prose">{description}</p>}
            </div>
            <div className={cx('min-w-0', !wide && 'max-w-2xl')}>{children}</div>
        </section>
    );
};

/**
 * The top of a page: its title in the display face, one line saying what it
 * is for, and the page's own actions on the right. `eyebrow` names the area
 * above the title ("Settings"); `meta` sits under it on every width — the
 * freshness line belongs there. Every page starts with one, so every page's
 * title sits in the same place.
 */
export const PageHeader = ({
    title, subtitle, actions, className, eyebrow, meta,
}: {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    className?: string;
    eyebrow?: React.ReactNode;
    meta?: React.ReactNode;
}) => (
    <div className={cx('flex items-end justify-between gap-x-6 gap-y-3 flex-wrap mb-6', className)}>
        {/* From 768px the title takes the room and its subtitle wraps, so the actions stay beside it rather
            than dropping under a long subtitle; if they must wrap, they keep to the right edge. */}
        <div className="min-w-0 md:grow md:basis-sm">
            {eyebrow && <div className={cx(eyebrowClass, 'mb-1.5')}>{eyebrow}</div>}
            <h2 className="font-display stretch-title text-title font-semibold text-label tracking-title leading-tight">{title}</h2>
            {/* The line under a title explains; on a phone it costs lines the list needs. */}
            {subtitle && <p className="text-body text-label-3 mt-1.5 max-md:hidden max-w-3xl">{subtitle}</p>}
            {meta && <div className="mt-2.5">{meta}</div>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap ml-auto">{actions}</div>}
    </div>
);

/** A heading inside a page: a group of fields, a list, a history. */
export const SectionHeader = ({
    title,
    subtitle,
    actions,
    className,
}: {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    className?: string;
}) => (
    <div className={cx('flex items-end justify-between gap-x-6 gap-y-2 flex-wrap', className)}>
        <div className="min-w-0">
            <h2 className="font-display stretch-title text-heading font-semibold text-label tracking-heading">{title}</h2>
            {subtitle && <p className="text-secondary text-label-3 mt-0.5 max-md:hidden">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
);

/* ---------------------------------- Chip ---------------------------------- */

/**
 * A chip: one of a few short choices laid out in a row — an outcome, a quick
 * date, a role to send to — or a small insert action beside a field (DESIGN-SYSTEM §7.3).
 * 32px and the control's radius, like a small button. A chosen chip is the
 * product's one "selected" look — the accent's tint, edge and ink, the same as
 * the chosen place in the navigation — never a solid navy block, which read as
 * a second primary button beside the real one (2026-10 global sweep). A toggle
 * carries `aria-pressed`; the words say what it is.
 */
export const chipClass = (pressed = false) => cx(
    // Fill, edge and colour change together, in one transition: nothing snaps while something else is still fading.
    'inline-flex items-center gap-1 h-8 px-3 rounded-control text-secondary font-semibold border whitespace-nowrap transition-[background-color,border-color,color] duration-150',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    pressed ? 'bg-accent-tint text-accent-text border-accent' : 'bg-card text-label-2 border-separator-strong hover:bg-hover hover:text-label',
);

/* ---------------------------- Segmented choice ---------------------------- */

/**
 * Two to four mutually exclusive views of the same thing — Everyone / Mine,
 * Light / Dark / System. Buttons that say whether they are pressed, in a
 * labelled group, drawn as one control: a track, and one raised mark that
 * slides to the chosen option (`useIndicator`). The words never change weight,
 * so nothing shifts as the choice moves; Left and Right move the choice, as in
 * a radio group. `equal` gives every option the same width (a short set of
 * words, or icons); `tone="chrome"` sits on the navy frame.
 */
export function Segmented<T extends string>({
    label, options, value, onChange, className, equal = false, tone = 'surface', size = 'md',
}: {
    label: string;
    /** `name` replaces the words as the option's accessible name (an icon-only option); `hint` is only its tooltip. */
    options: readonly { value: T; label: React.ReactNode; icon?: React.ReactNode; name?: string; hint?: string }[];
    value: T;
    onChange: (value: T) => void;
    className?: string;
    equal?: boolean;
    tone?: 'surface' | 'chrome';
    size?: 'sm' | 'md';
}) {
    const { track, box, animate } = useIndicator<HTMLDivElement>(value);
    const chrome = tone === 'chrome';
    // The mark takes the chosen option's place and size on both axes, so a set that wraps onto a second row on a
    // narrow screen (className "flex-wrap") still has one mark that moves to the chosen option (2026-10 global sweep).
    const style: React.CSSProperties | undefined = box
        ? { transform: `translate(${box.left}px, ${box.top}px)`, width: box.width, height: box.height }
        : undefined;
    const focusOn = (v: T) => track.current?.querySelector<HTMLButtonElement>(`[data-value=${JSON.stringify(v)}]`)?.focus();
    return (
        <div
            ref={track}
            role="group"
            aria-label={label}
            onKeyDown={arrowChoice(options.map(o => o.value), value, onChange, focusOn)}
            className={cx(
                'relative isolate p-0.5 rounded-control max-w-full',
                equal ? 'inline-grid grid-flow-col auto-cols-fr' : 'inline-flex items-center',
                chrome ? 'bg-chrome-2' : 'bg-segment-track',
                className,
            )}
        >
            {box && (
                <span
                    aria-hidden="true"
                    data-indicator=""
                    style={style}
                    className={cx(
                        'absolute left-0 top-0 -z-10 rounded-sm',
                        chrome ? 'bg-chrome-3 shadow-e1' : 'bg-segment-thumb shadow-segment',
                        animate && 'transition-[transform,width,height] duration-200 ease-out-quart',
                    )}
                />
            )}
            {options.map(o => {
                const on = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        data-value={o.value}
                        data-active={on}
                        aria-pressed={on}
                        aria-label={o.name}
                        title={o.hint ?? o.name}
                        tabIndex={on ? 0 : -1}
                        onClick={() => onChange(o.value)}
                        className={cx(
                            'inline-flex items-center justify-center gap-1.5 min-w-0 rounded-sm font-semibold whitespace-nowrap transition-colors duration-150',
                            size === 'sm' ? 'h-7 px-2.5 text-caption' : 'h-8 px-3 text-secondary',
                            on
                                ? (chrome ? 'text-chrome-label' : 'text-label')
                                : (chrome ? 'text-chrome-label-3 hover:text-chrome-label' : 'text-label-3 hover:text-label'),
                        )}
                    >
                        {o.icon}
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

/* ------------------------------ Empty state ------------------------------ */

/**
 * What an empty place says: what it is, and the one thing that fills it.
 *
 * Set on the content's own left edge — the 20px inset a panel's heading and a table's first column share — not
 * centred in a tall box (2026-10 global sweep): an empty list inside a card read as a large blank card with a
 * line of text floating in its middle. No icon tile: the sentence and the button are what somebody needs.
 * `compact` is a secondary list's empty line (no invitations, no sends yet); the default stands in for a whole
 * list or page.
 */
export const EmptyState = ({
    title,
    hint,
    action,
    compact = false,
    className,
}: {
    title: string;
    hint?: string;
    action?: React.ReactNode;
    /** Kept for older callers; an empty place carries no picture. */
    icon?: React.ReactNode;
    /** Inside a panel or a list, rather than in place of a whole page. */
    compact?: boolean;
    className?: string;
}) => (
    <div className={cx('px-5', compact ? 'py-5' : 'py-8', className)}>
        <p className={compact ? 'text-body font-semibold text-label' : 'font-display stretch-title text-heading font-semibold text-label tracking-heading'}>{title}</p>
        {hint && <p className={cx('text-label-3 mt-1 max-w-prose leading-relaxed', compact ? 'text-secondary' : 'text-body')}>{hint}</p>}
        {action && <div className={compact ? 'mt-3' : 'mt-4'}>{action}</div>}
    </div>
);

/* ------------------------------- Skeleton -------------------------------- */

/**
 * Placeholder rows while a list is being prepared: shaped like the rows they
 * stand in for, so the page does not jump when the real ones arrive, and
 * still rather than shimmering — this shows for a moment and should not draw
 * the eye.
 */
export const SkeletonRows = ({ rows = 8, className }: { rows?: number; className?: string }) => (
    <div className={cx('divide-y divide-separator', className)} aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-5 h-13">
                <div className="flex-1 min-w-0">
                    <div className="h-3 rounded-sm bg-card-3" style={{ width: `${38 + ((i * 13) % 34)}%` }} />
                    <div className="h-2.5 rounded-sm bg-card-2 mt-2" style={{ width: `${22 + ((i * 7) % 26)}%` }} />
                </div>
                <div className="h-3 w-20 rounded-sm bg-card-3 flex-none" />
                <div className="h-3 w-24 rounded-sm bg-card-2 flex-none hidden sm:block" />
                <div className="h-3 w-16 rounded-sm bg-card-2 flex-none hidden md:block" />
            </div>
        ))}
    </div>
);

/** Announced to a screen reader; the shapes above are decorative. */
export const LoadingList = ({ label = 'Loading', rows = 8 }: { label?: string; rows?: number }) => (
    <div role="status" aria-live="polite">
        <span className="sr-only">{label}</span>
        <SkeletonRows rows={rows} />
    </div>
);

/* -------------------------------- Loading -------------------------------- */

export const Spinner = ({ className }: { className?: string }) => (
    <svg className={cx('animate-spin', className)} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity=".2" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);
